import { useMemo } from 'react';
import { COLOR_BY_OPTIONS, legendFor, swatchFor } from '../lib/colors';
import { cn } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';

/** Beyond this the legend stops being a legend and starts being a list. */
const MAX_ENTRIES = 12;

/**
 * What the colours mean, for whichever property the cards are coloured by.
 *
 * Only rendered when colouring is on — an org chart with no colour coding needs
 * no key, and the space belongs to the chart.
 */
export function ColorLegend() {
  const colorBy = useAppStore((s) => s.colorBy);
  const people = useAppStore((s) => s.model?.people);
  const squads = useAppStore((s) => s.planSquads);
  const overrides = useAppStore((s) => s.colorOverrides);
  const setColorBy = useAppStore((s) => s.setColorBy);

  const entries = useMemo(
    () => (people ? legendFor(people, colorBy, squads) : []),
    [people, colorBy, squads],
  );

  if (colorBy === 'none' || entries.length === 0) return null;

  const shown = entries.slice(0, MAX_ENTRIES);
  const hidden = entries.length - shown.length;
  const label = COLOR_BY_OPTIONS.find((option) => option.value === colorBy)?.label ?? colorBy;

  return (
    <aside
      aria-label={`Colour key: ${label}`}
      className={cn(
        'pointer-events-auto max-w-[200px] rounded-xl border border-[var(--color-line)]',
        'bg-[var(--color-surface)]/95 p-2 shadow-[var(--shadow-card)] backdrop-blur',
      )}
    >
      <div className="flex items-center justify-between gap-2 px-1 pb-1.5">
        <p className="text-[10px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">{label}</p>
        <button
          type="button"
          aria-label="Clear colouring"
          onClick={() => setColorBy('none')}
          className="text-[10.5px] text-[var(--color-brand)] hover:underline"
        >
          Clear
        </button>
      </div>

      <ul className="max-h-[240px] space-y-0.5 overflow-y-auto">
        {shown.map((entry) => {
          return (
            <li key={entry.key} className="flex items-center gap-1.5 px-1 py-0.5">
              <span
                aria-hidden
                style={{ background: swatchFor(entry.key, overrides, colorBy) }}
                className="h-3.5 w-2 shrink-0 rounded-sm"
              />
              <span
                className="min-w-0 flex-1 truncate text-[11px] text-[var(--color-ink-2)]"
                title={entry.key}
              >
                {entry.key}
              </span>
              <span className="shrink-0 text-[10.5px] tabular-nums text-[var(--color-ink-3)]">
                {entry.count}
              </span>
            </li>
          );
        })}
      </ul>

      {hidden > 0 ? (
        <p className="px-1 pt-1 text-[10.5px] text-[var(--color-ink-3)]">+ {hidden} more, sharing colours</p>
      ) : null}
    </aside>
  );
}
